import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  getCountFromServer,
  or,
  and,
  limit,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { MessageTimestampToDate, Messages, Topic, TopicTimestampToDate } from "../model/Message";
import { ReplyButton } from "../model/ReplyButton";
import { Company } from "../model/Companies";
import { TopicsMap } from "../model/TopicsMap";

const messagesQuery = {
  addMessage: async (message: Messages, replyButtonSelected?: ReplyButton | undefined) => {
    console.log("message addMessage access");
    try{
      message.isMunicipality = false; // 団体からのお知らせフラグ
      if(message.link === '' && message.url !== undefined && message.url !== ''){
        message.link = message.url;
      };
      // リンク
      delete message.url;
      // 配信先グループ
      const topicList: string[] = [];
      if(message.topic !== undefined && message.topic.length > 0) {
        topicList.push(...message.topic);
      }
      // topicを除外
      delete message.topic;
      const docRef = await addDoc(collection(db, "messages"), message);
      console.log("Message Document written with ID: ", docRef.id);
      await updateDoc(docRef,{id:docRef.id});
      if(topicList.length > 0){
        await messagesQuery.setTopic(docRef.id, topicList, message);
      }
      if(replyButtonSelected !== undefined && replyButtonSelected.id !== 'undefined') {
        replyButtonSelected.messageId = docRef.id;
        await messagesQuery.setReplyButton(docRef.id, message, replyButtonSelected);
      }
      return docRef.id;
    } catch (err: any) {
      throw err;
    }
  },
  //指定したお知らせを取得
  getMessage: async (messageId: string) => {
    console.log("message getMessage access");
    const messageRef = await getDoc(doc(db, "messages", messageId));
    return MessageTimestampToDate(messageRef.data());
  },
  // お知らせに付加された返信ボタン取得
  getReplyButton: async (messageId: string) => {
    console.log("message getReplyButton access");
    const replyButtonRef =  await getDocs(collection(db, `messages/${messageId}/replyButtons`));
    return replyButtonRef.docs.map((item) => item.data() as ReplyButton)[0];
  },
  updateMessage: async (message: Messages, replyButtonSelected?: ReplyButton | undefined) => {
    console.log("message updateMessage access");
    try {
      const messageRef = doc(db, "messages", message.id);

      // 掲載終了日
      const publishEndDate = !Number.isNaN(message?.publishEndAt?.getTime()) && message?.publishEndAt?.getTime() !== undefined
      ? Timestamp.fromDate(message?.publishEndAt ?? new Date())
      : deleteField();

      // 配信先グループ
      const topicList: string[] = [];
      if(message.topic !== undefined && message.topic.length > 0) {
        topicList.push(...message.topic);
      }

      await updateDoc(messageRef, {
        title: message.title,
        body: message.body,
        urlButtonFlg: message.urlButtonFlg !== undefined ? message.urlButtonFlg : deleteField(),
        urlButtonName: message.urlButtonFlg !== undefined ? message.urlButtonName : deleteField(),
        urlLink: message.urlButtonFlg !== undefined ? message.urlLink : deleteField(),
        image: message.image,
        contactInformation: message.contactInformation,
        eventVenue: message.eventVenue,
        type: message.type,
        link: message.link,
        fileupload: message.fileupload,
        select:message.select,
        draftFlg: message.draftFlg,
        authorUid: message.authorUid,
        deliveredFlg: message.deliveredFlg,
        eventFlg: message.eventFlg,
        eventAt: Timestamp.fromDate(message.eventAt),
        eventEndAt: Timestamp.fromDate(message.eventEndAt),
        eventType: message.eventType,
        publishEndAt: publishEndDate,
        sendDate: Timestamp.fromDate(message.sendDate),
        createAt: Timestamp.fromDate(message.createAt),
        updateAt: serverTimestamp(),
      });

      // 登録されているTopicの更新
      const topicRef = await getDocs(collection(db, `messages/${message.id}/topic`));
      const targetDeleteTopicId: string[] = []
      if(topicRef !== undefined){
        const topicListSet = new Set(topicList);
        topicRef.docs.forEach(doc => {
          if (!topicListSet.has(doc.id)) {
            targetDeleteTopicId.push(doc.id);
          }
        });
        await messagesQuery.deleteTopic(message.id, targetDeleteTopicId);
      }
      if(topicList.length > 0){
        await messagesQuery.setTopic(message.id, topicList, message);
      }
      // 前に登録されているreplyButtonを一旦削除する
      const replyButtonRef = await getDocs(collection(db, `messages/${message.id}/replyButtons`));
      if(replyButtonRef !== undefined) {
        await Promise.all(replyButtonRef.docs.map(async (document): Promise<void> => {
          await deleteDoc(doc(db,document.ref.path));
        }));
      }
      if(replyButtonSelected !== undefined && replyButtonSelected.id !== "undefined") {
        replyButtonSelected.messageId = message.id;
        await messagesQuery.setReplyButton(message.id, message, replyButtonSelected);
      }
    } catch (err) {
      throw err;
    }
  },
  deleteMessage:  async (id: string) => {
    console.log("message deleteMessage access");
    try {
      await deleteDoc(doc(db, "messages", id));
    } catch (err) {
      throw err;
    }
  },
  // Topic削除
  deleteTopic: async (messageId: string, topicId: string[] = []) => {
    console.log("message deleteTopic access");
    try {
      let batchCount = 0;

      if (topicId.length > 0) {
        // topicIdを渡された場合、それを削除
        for (let i = 0; i < topicId.length; i += 500) {
          const batch = writeBatch(db);
          const chunk = topicId.slice(i, i + 500);

          chunk.forEach((id) => {
            batch.delete(doc(db, `messages/${messageId}/topic`, id));
          });

          await batch.commit();
          batchCount++;
          console.log(`Batch ${batchCount} committed (${chunk.length} docs).`);
        }
      } else {
        // topicIdを渡されなかった場合、全て削除
        const querySnapshot = await getDocs(collection(db, `messages/${messageId}/topic`));
        const docs = querySnapshot.docs;

        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(db);
          const chunk = docs.slice(i, i + 500);

          chunk.forEach((document) => {
            batch.delete(doc(db, `messages/${messageId}/topic`, document.id));
          });

          await batch.commit();
          batchCount++;
          console.log(`Batch ${batchCount} committed (${chunk.length} docs).`);
        }
      }
    } catch (err) {
      console.error("Error deleting topic:", err);
      throw err;
    }
  },
  // お知らせtopic登録
  setTopic: async (messageId: string, topic: string[], message: Messages) => {
    console.log("message setTopic access");
    try {
      const now = new Date();
      const chunkSize = 500;

      for (let i = 0; i < topic.length; i += chunkSize) {
        const chunk = topic.slice(i, i + chunkSize);
        const batch = writeBatch(db);

        chunk.forEach((item) => {
          const topicRef = doc(db, `messages/${messageId}/topic/${item}`);
          const topicData: Topic = {
            id: item,
            tenantId: message.tenantId,
            messageId: messageId,
            draftFlg: message.draftFlg,
            deliveredFlg: message.deliveredFlg,
            sendDate: message.sendDate,
            eventFlg: message.eventFlg,
            eventAt: message.eventFlg ? message.eventAt : null,
            updateAt: now
          };
          batch.set(topicRef, topicData);
        });

        await batch.commit();
      }
    } catch (err) {
      console.error(err);
    }
  },
  setReplyButton: async (messageId: string, message: Messages, replyButton: ReplyButton) => {
    console.log("message setReplyButton access");
    try {
      // sendDate を追加
      replyButton.sendDate = message.sendDate;
      // eventAt を追加
      replyButton.eventAt = message.eventAt;

      await addDoc(collection(db, `messages/${messageId}/replyButtons`), replyButton);
    } catch (err) {
      console.error(err);
    }
  },
  getMessagesReplyButton: async (tenantId: string) => {
    console.log("message getMessagesReplyButton access");
    const replyButtonRef = await getDocs(
      query(collectionGroup(db, `replyButtons`), where("tenantId", "==", tenantId)));
    return replyButtonRef.docs.map((item) => item.data() as ReplyButton);
  },
  getMessagesList: async (authorUid: string | undefined, company: Company) => {
    console.log("message getMessagesList access");
    const returnMessage: Messages[] = [];
    const messagesRef = await getDocs(
      query(collection(db, "messages"),
        where("topic", "array-contains-any", ["all", company.displayId]),
        where("isMunicipality", '!=', true), // 団体からのお知らせを表示しない
      ));
    await Promise.all(messagesRef.docs.map(async (doc) => {
      if(!doc.data().draftFlg) returnMessage.push(MessageTimestampToDate(doc.data()));
      if(doc.data().draftFlg && doc.data().authorUid === authorUid) returnMessage.push(MessageTimestampToDate(doc.data()));
    }));
    return returnMessage;

/*TODO 改善版    
    const messagesRef = await getDocs(
      query(
        collection(db, "messages"),
        or(
          and (
            where("tenantId", "==", company.id),
            where("isMunicipality", '==', false),
            where("draftFlg", '==', false)
          ),
          and (
            where("tenantId", "==", company.id),
            where("isMunicipality", '==', false),
            where("draftFlg", '==', true),
            where("authorUid", "==", authorUid)
          ),
        ),
      )
    );

    return messagesRef.docs.map((doc) => MessageTimestampToDate(doc.data()));
*/
  },
  // グループ宛てお知らせ取得
  getGroupMessageList: async (authorUid: string | undefined, company: Company, topic: TopicsMap[]) => {
    console.log("message getGroupMessageList access");
    const returnMessage: Messages[] = [];
    const topicIdList = topic.map((tp) => tp.id);
    const filteredTopicIdList = topicIdList.filter((tp) => tp !== company.displayId);
    // 10件ごとに配列に入れる
    for(let i = 0; i < filteredTopicIdList.length / 10; i ++){
      const copyList = filteredTopicIdList.slice(i * 10, i * 10 + 10);
      const messagesRef = await getDocs(query(collection(db, "messages"),
        where("topic", "array-contains-any", copyList),
        where("isMunicipality", '!=', true), // 団体からのお知らせを表示しない
      ));
      await Promise.all(messagesRef.docs.map(async (doc) => {
        if(!doc.data().draftFlg) returnMessage.push(MessageTimestampToDate(doc.data()));
        if(doc.data().draftFlg && doc.data().authorUid === authorUid) returnMessage.push(MessageTimestampToDate(doc.data()));
      }));
    }
    return returnMessage;
  },

  // 今月の投稿数の取得
  getCurrentMonthSendMessageCount: async (companyId: string): Promise<number> => {
    console.log("message getCurrentMonthSendMessageCount access");
    const nowDate = new Date();
    const dateFrom = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1, 0, 0);
    const dateTo = new Date(nowDate.getFullYear(), nowDate.getMonth() + 1);

        // 当月配信済の件数
        const refDelivered = await getCountFromServer(
          query(
            collection(db, 'messages'), 
            where('tenantId', '==', companyId),
            where('isMunicipality', '==', true),  // 団体の配信
            where('draftFlg', '==', false),       // 下書以外
            where('sendDate', '>=', dateFrom),    // 当月の配信
            where('sendDate', '<', dateTo),
            where('deliveredFlg', '==', true),    // 配信済
          )
        );
    
        // 当月配信し掲載終了済の件数
        const refPublishEnd = await getCountFromServer(
          query(
            collection(db, 'messages'), 
            where('tenantId', '==', companyId),
            where('isMunicipality', '==', true),  // 団体の配信
            where('draftFlg', '==', false),       // 下書以外
            where('sendDate', '>=', dateFrom),    // 当月の配信
            where('sendDate', '<', dateTo),
            where('deliveredFlg', '==', false),   // 未配信
            where('publishEndAt', '<', nowDate),  // 掲載終了済
          )
        );
    
        return refDelivered.data().count + refPublishEnd.data().count;
/*
    const docsRef = await getDocs(
      query(collection(db, 'messages'), 
      where('tenantId', '==', companyId), 
      where('isMunicipality', '==', true),  // 団体の配信
      where('draftFlg', '==', false),       // 下書以外
      where('sendDate', '>=', dateFrom),    // 当月の配信
      where('sendDate', '<', dateTo),
    ));

    // 判定項目のみ取得（掲載終了日は timestamp → Date に変換）
    const messages = docsRef.docs.map((messages) => {
      return {
        publishEndAt : messages.data().publishEndAt !== undefined ? new Date(messages.data().publishEndAt.seconds * 1000) : undefined,
        deliveredFlg : messages.data().deliveredFlg
      }
    });

    // 配信済 or 掲載終了済を抽出
    const filteredMsg
        = messages.filter((msg) => (msg.deliveredFlg === true)
                                || (msg.deliveredFlg === false
                                    && (msg.publishEndAt !== undefined && msg.publishEndAt < nowDate)));

    return filteredMsg.length;
*/
  },
  // お知らせに付加されたTopic取得
  getTopic: async (messageId: string) => {
    console.log("message getTopic access");
    const topicList =  await getDocs(collection(db, `messages/${messageId}/topic`));
    return topicList.docs.map((doc) => TopicTimestampToDate(doc.data()) as Topic);
  },

};

export default messagesQuery;
